import {
  createElement,
  forwardRef,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';
import { extractSystemProps } from 'src/components/md/system-props';

/**
 * Tailwind/style-based replacement for the former `styled(Box)` MDBox root.
 * Reproduces the MD color/gradient/shadow/borderRadius tokens as inline CSS and
 * delegates all MUI system props + `sx` to {@link extractSystemProps}.
 */

interface OwnerState {
  variant: 'contained' | 'gradient';
  bgColor: string;
  color: string;
  opacity: number;
  borderRadius: string;
  shadow: string;
  coloredShadow: string;
}

// MD palette mains (matches src/assets/md-theme/base/colors.ts)
const COLORS: Record<string, string> = {
  transparent: 'transparent',
  white: '#ffffff',
  black: '#000000',
  primary: '#1c1917', // stone-900
  secondary: '#57534e', // stone-600
  info: '#0ea5e9', // sky-500
  success: '#84cc16', // lime-500
  warning: '#eab308', // yellow-500
  error: '#ef4444', // red-500
  light: '#e7e5e4', // stone-200
  dark: '#1c1917', // stone-900
  text: '#292524', // stone-800
  'grey-100': '#f5f5f4',
  'grey-200': '#e7e5e4',
  'grey-300': '#d6d3d1',
  'grey-400': '#a8a29e',
  'grey-500': '#78716c',
  'grey-600': '#57534e',
  'grey-700': '#44403c',
  'grey-800': '#292524',
  'grey-900': '#1c1917',
};

const GRADIENT_STATES: Record<string, string> = {
  primary: '#0c0a09',
  secondary: '#44403c',
  info: '#0284c7',
  success: '#65a30d',
  warning: '#ca8a04',
  error: '#dc2626',
  dark: '#0c0a09',
  light: '#d6d3d1',
};

const BORDER_RADII: Record<string, string> = {
  xs: '0.1rem',
  sm: '0.125rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  xxl: '1rem',
  section: '10rem',
};

const BOX_SHADOWS: Record<string, string> = {
  xs: '0 2px 9px -5px rgba(0,0,0,0.15)',
  sm: '0 5px 10px rgba(0,0,0,0.12)',
  md: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
  lg: '0 8px 26px -4px rgba(0,0,0,0.15), 0 8px 9px -5px rgba(0,0,0,0.06)',
  xl: '0 23px 45px -11px rgba(0,0,0,0.25)',
  xxl: '0 20px 27px 0 rgba(0,0,0,0.05)',
  inset: 'inset 0 1px 2px rgba(0,0,0,0.075)',
};

function resolveBackground(
  variant: string,
  bgColor: string,
): string | undefined {
  if (variant === 'gradient') {
    const main = COLORS[bgColor] ?? bgColor;
    const state = GRADIENT_STATES[bgColor] ?? main;
    return `linear-gradient(195deg, ${main}, ${state})`;
  }
  if (bgColor in COLORS)
    return bgColor === 'transparent' ? undefined : COLORS[bgColor];
  return bgColor;
}

interface Props {
  ownerState: OwnerState;
  component?: ElementType;
  className?: string;
  children?: ReactNode;
  [key: string]: unknown;
}

const MDBoxRoot = forwardRef<HTMLElement, Props>(
  function MDBoxRoot(props, ref) {
    const { ownerState, component, className, children, ...rest } =
      props as Props & { ownerState: OwnerState };
    const {
      variant,
      bgColor,
      color,
      opacity,
      borderRadius,
      shadow,
      coloredShadow,
    } = ownerState;

    const ownerStyle: CSSProperties = {};
    const background = resolveBackground(variant, bgColor);
    if (background) ownerStyle.background = background;
    if (color && color !== 'dark') ownerStyle.color = COLORS[color] ?? color;
    if (opacity !== 1) ownerStyle.opacity = opacity;
    if (borderRadius && borderRadius !== 'none') {
      ownerStyle.borderRadius = BORDER_RADII[borderRadius] ?? borderRadius;
    }
    if (shadow && shadow !== 'none')
      ownerStyle.boxShadow = BOX_SHADOWS[shadow] ?? shadow;
    else if (coloredShadow && coloredShadow !== 'none') {
      ownerStyle.boxShadow = '0 4px 20px 0 rgba(0,0,0,0.14)';
    }

    const { style, rest: domProps } = extractSystemProps(rest);
    const Component = (component ?? 'div') as ElementType;

    return createElement(
      Component,
      {
        ref,
        className: className as string | undefined,
        style: { ...ownerStyle, ...style },
        ...domProps,
      },
      children as ReactNode,
    );
  },
);

export default MDBoxRoot;
